import React from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MigrationStatus, MigrationStatusColors } from '../../../models/MigrationStatus';

interface MigrationStatisticsProps {
  totalNumberOfFiles: number;
  filesMigrated: number;
  filesFlagged: number;
  filesBlockedByEntityFlag: number;
  filesInterruptedInitial: number;
  filesInterruptedDelta: number;
  filesModified: number;
  filesNotFound: number;
  filesNotFoundMarkedForRetry: number;
  unresolvedConflictCount: number;
  acknowledgedConflictCount: number;
  filesSizeInMb: number;
  filesSkippedDuplicate?: number;
}

export const MigrationEntityShortStatistics: React.FC<MigrationStatisticsProps> = ({
  totalNumberOfFiles,
  filesMigrated,
  filesFlagged,
  filesBlockedByEntityFlag,
  filesInterruptedInitial,
  filesInterruptedDelta,
  filesModified,
  filesNotFound,
  filesNotFoundMarkedForRetry,
  unresolvedConflictCount,
  acknowledgedConflictCount,
  filesSizeInMb,
  filesSkippedDuplicate,
}: MigrationStatisticsProps) => {
  const skippedDuplicateFiles = filesSkippedDuplicate ?? 0;

  // The badges below partition the file total, so "Not Started" is what is left once every other bucket is
  // accounted for: files never attempted (NotStarted/InProgress). Every bucket the stat views count has to be
  // subtracted here or those files are reported twice — once in their own badge, once as not started.
  const notStartedFiles = Math.max(0, totalNumberOfFiles - (
    filesMigrated +
    filesFlagged +
    filesInterruptedInitial +
    filesInterruptedDelta +
    filesModified +
    filesNotFound +
    filesNotFoundMarkedForRetry +
    unresolvedConflictCount +
    acknowledgedConflictCount +
    skippedDuplicateFiles
  ));

  // filesFlagged covers two kinds of hold that need different remedies: the file's own DICOM/extension flag,
  // which is resolved per file, and a flag on the parent entity, which holds every file under it until an
  // operator resolves that one flag. filesBlockedByEntityFlag is the parent-flag subset of filesFlagged.
  const filesFlaggedByOwnFlag = Math.max(0, filesFlagged - filesBlockedByEntityFlag);

  const migratedPercentage = totalNumberOfFiles > 0
    ? ((filesMigrated + skippedDuplicateFiles) / totalNumberOfFiles) * 100
    : 0;

  return (
    <div className="flex flex-1 flex-row flex-wrap items-center justify-start gap-2">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Not Started</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.NotStarted]}>
          {String(notStartedFiles)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Migrated</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.Finished]}>
          {String(filesMigrated)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Interrupted Initial</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.InterruptedInitial]}>
          {String(filesInterruptedInitial)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Interrupted Delta</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.InterruptedDelta]}>
          {String(filesInterruptedDelta)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Modified</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.Modified]}>
          {String(filesModified)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Not Found</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.NotFound]}>
          {String(filesNotFound)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Not Found (Marked For Retry)</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.NotFoundMarkedForRetry]}>
          {String(filesNotFoundMarkedForRetry)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Flagged</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.Flagged]}>
          {String(filesFlaggedByOwnFlag)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Blocked by flag</Label>
        <Badge variant="outline-primary">
          {String(filesBlockedByEntityFlag)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Blocked by conflict</Label>
        <Badge variant="danger">
          {String(unresolvedConflictCount)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Acknowledged conflict</Label>
        <Badge variant="warning">
          {String(acknowledgedConflictCount)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Skipped Duplicate</Label>
        <Badge variant={MigrationStatusColors[MigrationStatus.SkippedDuplicate]}>
          {String(skippedDuplicateFiles)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Files Count</Label>
        <Badge variant="neutral">
          {String(totalNumberOfFiles)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Size (MB)</Label>
        <Badge variant="primary">
          {filesSizeInMb.toFixed(2)}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        <Label className="text-xs">Progress</Label>
        <Badge
          variant={migratedPercentage > 99.99 ? "success" : "primary"}
          className="text-center"
        >
          {migratedPercentage % 1 > 0.99 || migratedPercentage % 1 < 0.01
            ? `${migratedPercentage.toFixed(0)}%`
            : `${migratedPercentage.toFixed(2)}%`}
        </Badge>
      </div>
    </div>
  );
};

export default MigrationEntityShortStatistics;
